import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getOpenCountByApp, getLatestTickets } from '../services/ticketService';
import { useApp } from '../context/AppContext';
import { getActiveAppOriginCodes, getAppOriginColor, getAppOriginLabel } from '../services/appOriginService';
import TicketProtocolLabel from '../components/shared/TicketProtocolLabel';
import OpenCountSparkline from '../components/dashboards/OpenCountSparkline';
import PageHeader from '../components/shared/PageHeader';
import CreateTicketButton from '../components/shared/CreateTicketButton';
import { useCreateTicketModal } from '../context/CreateTicketModalContext';
import styled, { css, keyframes } from 'styled-components';
import { ArrowRight, ArrowUpRight, ChevronDown, ChevronUp, Inbox } from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-x: hidden;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const skeletonFill = css`
  background: linear-gradient(
    90deg,
    var(--color-bg-tertiary) 25%,
    var(--color-border-primary) 50%,
    var(--color-bg-tertiary) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: 6px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Skeleton = styled.span`
  display: block;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  ${skeletonFill}
`;

const BelowFormSection = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  align-items: start;
  width: 100%;

  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
    gap: 1.5rem;
  }
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  min-width: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  min-height: 28px;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-text-primary);
`;

const SectionMeta = styled.span`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  margin-right: -0.5rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;

  text-decoration: none;

  &:visited {
    color: var(--color-text-secondary);
  }

  &:hover {
    background: var(--color-hover-overlay);
    color: var(--color-text-primary);
  }
`;

const CARD_MIN_WIDTH = 240;
const CARDS_GAP = 14;

const CardsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: ${CARDS_GAP}px;
  width: 100%;
`;

const useGridColumns = (ref) => {
  const [cols, setCols] = useState(3);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      setCols(Math.max(1, Math.floor((width + CARDS_GAP) / (CARD_MIN_WIDTH + CARDS_GAP))));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return cols;
};

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
`;

const CardsEmpty = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  background: var(--color-bg-primary);
  border: 1px dashed var(--color-border-secondary);
  border-radius: 12px;
  color: var(--color-text-muted);
  font-size: 0.875rem;
`;

const CardArrow = styled(ArrowUpRight)`
  flex-shrink: 0;
  color: var(--color-text-muted);
  opacity: 0;
  transform: translate(-2px, 2px);
  transition: opacity 0.15s ease, transform 0.15s ease;
`;

const CountCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
  padding: 1rem 1.125rem 0.875rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--color-border-secondary);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover ${CardArrow}, &:focus-visible ${CardArrow} {
    opacity: 1;
    transform: translate(0, 0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`;

const AppName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const AppDot = styled.i`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--color-border-accent)'};
`;

const CountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin-top: -0.25rem;
`;

const CountValue = styled.span`
  font-size: 1.875rem;
  font-weight: 650;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-primary);
`;

const CountLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
`;

const CardStats = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 0;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border-primary);
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;

  dt {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    white-space: nowrap;
  }

  dd {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--color-text-primary);
  }
`;

const CardsError = styled.div`
  padding: 1rem;
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 8px;
  color: #dc3545;
  font-size: 0.875rem;
`;

const LastTicketsBox = styled.div`
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  overflow: hidden;
`;

const LastTicketsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.375rem;
  display: flex;
  flex-direction: column;
`;

const LastTicketItem = styled.li`
  & + & {
    border-top: 1px solid var(--color-border-primary);
  }
`;

const LastTicketLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  margin: 1px 0;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease;

  &:visited {
    color: inherit;
  }

  &:hover {
    background: var(--color-hover-overlay);
  }

  &:focus-visible {
    outline: 2px solid var(--color-text-primary);
    outline-offset: -2px;
  }
`;

const LastTicketDot = styled.i`
  width: 8px;
  height: 8px;
  margin-top: 0.4rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--color-border-accent)'};
`;

const LastTicketBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`;

const LastTicketSubject = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LastTicketMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  font-size: 0.75rem;
  color: var(--color-text-muted);

  > span {
    white-space: nowrap;
  }
`;

const MetaSeparator = styled.span`
  color: var(--color-border-accent);
`;

const LastTicketTime = styled.time`
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const LastTicketSkeletonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1.125rem;

  & + & {
    border-top: 1px solid var(--color-border-primary);
  }
`;

const LastTicketsError = styled.div`
  margin: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 8px;
  color: #dc3545;
  font-size: 0.875rem;
`;

const LastTicketsEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;

  svg {
    color: var(--color-border-accent);
  }
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { handleAppOriginChange, handlePageChange, handleStatusChange, appOrigins } = useApp();
  const [countByApp, setCountByApp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestTickets, setLatestTickets] = useState([]);
  const [latestLoading, setLatestLoading] = useState(true);
  const [latestError, setLatestError] = useState(null);
  const [showAllOpenCards, setShowAllOpenCards] = useState(false);
  const { createdTicketsVersion } = useCreateTicketModal();
  const openColumnRef = useRef(null);
  const cardCols = useGridColumns(openColumnRef);
  const collapsedCardLimit = cardCols === 1 ? 4 : cardCols * 2;

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOpenCountByApp();
      setCountByApp(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching open count by app:', err);
      setError(err?.message || 'Erro ao carregar contagens.');
      setCountByApp([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLatest = useCallback(async () => {
    setLatestLoading(true);
    setLatestError(null);
    try {
      const data = await getLatestTickets(6);
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      setLatestTickets(list);
    } catch (err) {
      console.error('Error fetching latest tickets:', err);
      setLatestError(err?.message || 'Erro ao carregar últimos tickets.');
      setLatestTickets([]);
    } finally {
      setLatestLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, createdTicketsVersion]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest, createdTicketsVersion]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(parseISO(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return formatDistanceToNowStrict(parseISO(dateString), { locale: ptBR, addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const fixedOrigins = React.useMemo(() => getActiveAppOriginCodes(), [appOrigins]);
  const countMap = React.useMemo(() => {
    return (countByApp || []).reduce((acc, item) => {
      acc[item.appOrigin] = item.count != null ? item.count : 0;
      return acc;
    }, {});
  }, [countByApp]);
  const rowsByOrigin = React.useMemo(() => {
    return (countByApp || []).reduce((acc, item) => {
      if (item?.appOrigin) acc[item.appOrigin] = item;
      return acc;
    }, {});
  }, [countByApp]);
  const originsByOpenCount = React.useMemo(
    () => [...fixedOrigins].sort((a, b) => (countMap[b] ?? 0) - (countMap[a] ?? 0)),
    [fixedOrigins, countMap]
  );
  const visibleOrigins = showAllOpenCards
    ? originsByOpenCount
    : originsByOpenCount.slice(0, collapsedCardLimit);
  const hasMoreOpenCards = originsByOpenCount.length > collapsedCardLimit;
  const totalOpen = React.useMemo(
    () => originsByOpenCount.reduce((sum, origin) => sum + (countMap[origin] ?? 0), 0),
    [originsByOpenCount, countMap]
  );

  return (
    <PageContainer>
      <ContentContainer>
        <PageHeader
          title="Início"
          description="Tickets em aberto por aplicação e os últimos tickets criados."
          actions={<CreateTicketButton />}
        />

        <BelowFormSection>
          <Column ref={openColumnRef} aria-labelledby="home-open-title">
            <SectionHeader>
              <SectionHeading>
                <SectionTitle id="home-open-title">Tickets em aberto</SectionTitle>
                {!loading && !error && (
                  <SectionMeta>
                    {totalOpen} {totalOpen === 1 ? 'ticket' : 'tickets'} · {originsByOpenCount.length}{' '}
                    {originsByOpenCount.length === 1 ? 'aplicação' : 'aplicações'}
                  </SectionMeta>
                )}
              </SectionHeading>
              {!loading && !error && hasMoreOpenCards && (
                <TextButton
                  type="button"
                  onClick={() => setShowAllOpenCards((open) => !open)}
                  aria-expanded={showAllOpenCards}
                >
                  {showAllOpenCards ? 'Ver menos' : `Ver todas (${originsByOpenCount.length})`}
                  {showAllOpenCards ? (
                    <ChevronUp size={14} strokeWidth={2} aria-hidden />
                  ) : (
                    <ChevronDown size={14} strokeWidth={2} aria-hidden />
                  )}
                </TextButton>
              )}
            </SectionHeader>
            {loading && (
              <CardsSection $cols={cardCols} aria-busy="true" aria-label="Carregando tickets em aberto">
                {Array.from({ length: collapsedCardLimit }).map((_, i) => (
                  <SkeletonCard key={i}>
                    <Skeleton $w="45%" $h="12px" />
                    <Skeleton $w="30%" $h="28px" />
                    <Skeleton $h="40px" />
                    <Skeleton $w="80%" $h="12px" />
                  </SkeletonCard>
                ))}
              </CardsSection>
            )}
            {error && <CardsError role="alert">{error}</CardsError>}
            {!loading && !error && originsByOpenCount.length === 0 && (
              <CardsEmpty>Nenhuma aplicação cadastrada para exibir tickets em aberto.</CardsEmpty>
            )}
            {!loading && !error && originsByOpenCount.length > 0 && (
              <CardsSection $cols={cardCols}>
                {visibleOrigins.map((appOrigin) => {
                  const row = rowsByOrigin[appOrigin];
                  const accent = getAppOriginColor(appOrigin);
                  const label = getAppOriginLabel(appOrigin);
                  const openCount = countMap[appOrigin] ?? 0;
                  const openTickets = () => {
                    handleStatusChange(['OPEN']);
                    handleAppOriginChange([appOrigin]);
                    handlePageChange(1);
                    navigate(`/all-tickets?appOrigin=${appOrigin}`);
                  };
                  return (
                  <CountCard
                    key={appOrigin}
                    role="link"
                    tabIndex={0}
                    aria-label={`${label}: ${openCount} tickets em aberto. Ver tickets`}
                    onClick={openTickets}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openTickets();
                      }
                    }}
                  >
                    <CardHeader>
                      <AppName>
                        <AppDot $color={accent} aria-hidden />
                        <span>{label}</span>
                      </AppName>
                      <CardArrow size={16} strokeWidth={2} aria-hidden />
                    </CardHeader>
                    <CountRow>
                      <CountValue>{openCount}</CountValue>
                      <CountLabel>em aberto</CountLabel>
                    </CountRow>
                    <OpenCountSparkline dailyCounts={row?.dailyCounts} color={accent} />
                    <CardStats>
                      <Stat>
                        <dt>Bugs</dt>
                        <dd>{row?.bugCount ?? 0}</dd>
                      </Stat>
                      <Stat>
                        <dt>Outros</dt>
                        <dd>{row?.otherCount ?? 0}</dd>
                      </Stat>
                      {row?.last7DaysCount != null && (
                        <Stat>
                          <dt>Média 7 dias</dt>
                          <dd>{row.last7DaysCount}</dd>
                        </Stat>
                      )}
                    </CardStats>
                  </CountCard>
                  );
                })}
              </CardsSection>
            )}
          </Column>

          <Column aria-labelledby="home-latest-title">
            <SectionHeader>
              <SectionTitle id="home-latest-title">Últimos tickets criados</SectionTitle>
              <TextButton as={Link} to="/all-tickets">
                Ver todos
                <ArrowRight size={14} strokeWidth={2} aria-hidden />
              </TextButton>
            </SectionHeader>
            <LastTicketsBox>
              {latestLoading && (
                <div aria-busy="true" aria-label="Carregando últimos tickets">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <LastTicketSkeletonRow key={i}>
                      <Skeleton $w="8px" $h="8px" style={{ borderRadius: '50%', marginTop: 6 }} />
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Skeleton $w="85%" $h="12px" />
                        <Skeleton $w="50%" $h="10px" />
                      </div>
                    </LastTicketSkeletonRow>
                  ))}
                </div>
              )}
              {latestError && <LastTicketsError role="alert">{latestError}</LastTicketsError>}
              {!latestLoading && !latestError && latestTickets.length === 0 && (
                <LastTicketsEmpty>
                  <Inbox size={24} strokeWidth={1.75} aria-hidden />
                  Nenhum ticket criado ainda.
                </LastTicketsEmpty>
              )}
              {!latestLoading && !latestError && latestTickets.length > 0 && (
                <LastTicketsList>
                  {latestTickets.map((ticket) => {
                    const createdAt = ticket.creationDate || ticket.createdAt;
                    const appLabel = getAppOriginLabel(ticket.appOrigin);
                    return (
                      <LastTicketItem key={ticket.id}>
                        <LastTicketLink to={`/all-tickets/${ticket.id}`}>
                          <LastTicketDot $color={getAppOriginColor(ticket.appOrigin)} aria-hidden />
                          <LastTicketBody>
                            <LastTicketSubject>
                              {ticket.subjectDescription || ticket.subject || 'Sem assunto'}
                            </LastTicketSubject>
                            <LastTicketMeta>
                              <TicketProtocolLabel ticket={ticket} compact />
                              {appLabel ? (
                                <>
                                  <MetaSeparator aria-hidden>·</MetaSeparator>
                                  <span>{appLabel}</span>
                                </>
                              ) : null}
                            </LastTicketMeta>
                          </LastTicketBody>
                          <LastTicketTime dateTime={createdAt || undefined} title={formatDate(createdAt)}>
                            {formatRelativeDate(createdAt)}
                          </LastTicketTime>
                        </LastTicketLink>
                      </LastTicketItem>
                    );
                  })}
                </LastTicketsList>
              )}
            </LastTicketsBox>
          </Column>
        </BelowFormSection>
      </ContentContainer>
    </PageContainer>
  );
};

export default HomePage;
